import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useSpring, useInView } from "framer-motion";

export default function About() {
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);

  const timelineData = [
    {
      year: "Inspired by Our Vision",
      description: "To become the most preferred and trusted manufacturing and solutions partner for brands operating across diverse verticals. We aspire to lead with innovation, delivering exceptional quality and reliability, while empowering businesses to achieve their goals. By continuously adapting to market dynamics and exceeding expectations, we aim to be a cornerstone of success for our partners globally.",
    },
    {
      year: "Driven by Our Mission",
      description: "Customer First – Strengthen customer partnerships by providing products and services of the greatest value through innovation and excellence.",
    },
    {
      year: "Joined by Our Values",
      description: "We expanded our services to include checking and savings accounts with no monthly fees or minimum balance requirements.",
    },
  ];

  return (
    <div ref={containerRef} className="relative bg-gradient-to-b from-orange-50/30 to-white text-gray-900 overflow-hidden">
      {/* Hero Section with Parallax */}
      <div className="relative h-[80vh] lg:h-[70vh] flex items-center justify-center overflow-hidden">
        <motion.div 
          style={{ y, opacity, scale }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-orange-100/30 via-orange-50/20 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,146,60,0.07),transparent_70%)]" />
        </motion.div>

        <div ref={headerRef} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-center"
          >
            <motion.div
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="mb-8"
            >
              <h1 className="text-5xl md:text-7xl lg:text-6xl font-bold mb-6">
                Our
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500">
                  {" "}Vision{" "}&
                </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500">
                  {" "}Mission
                </span>
              </h1>
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="h-1 bg-gradient-to-r from-orange-500 to-yellow-400 mx-auto rounded-full mb-6"
              />
            </motion.div>
            
            <p className="text-xl md:text-2xl lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Empowering progress through innovation, guided by our vision for a smarter and sustainable future.
            </p>
          </motion.div>
        </div>
      </div>

      <section id="vision-mission" className="py-24 bg-gradient-to-b from-white via-orange-50/30 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">Who We Are</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Building the future of manufacturing excellence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" >
            {timelineData.map((item, index) => (
              <div
                key={index}
                className="bg-white/80 backdrop-blur-sm p-8 rounded-xl group hover:shadow-xl transition-all duration-300 border border-orange-100/50"
              >
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-50 rounded-full flex items-center justify-center group-hover:from-orange-200 group-hover:to-orange-100 transition-colors duration-300">
                    {index === 0 && (
                      <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                    {index === 1 && (
                      <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                    {index === 2 && (
                      <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    )}
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.year}</h3>
                {index === 0 && (
                  <p className="text-gray-600 leading-relaxed">
                    <ul>
                      <li>To become the most <span className="text-orange-500">preferred and trusted manufacturing and solutions partner</span> for brands operating across diverse verticals.</li>
                      <li>We aspire to lead with <span className="text-orange-500">innovation, delivering exceptional quality and reliability</span>, while empowering businesses to achieve their goals.</li>
                      <li>By continuously adapting to market dynamics and exceeding expectations, we aim to be a cornerstone of success for our partners globally.</li>
                    </ul>
                  </p>
                )}
                {index === 1 && (
                  <p className="text-gray-600 leading-relaxed">
                    <ul>
                      <li>Our mission is rooted in a relentless pursuit of excellence, ensuring that every product and service reflects our dedication to<span className="text-orange-500">quality, innovation, and customer success</span>.</li>
                      <li><span className="text-orange-500">Customer First </span>– At the heart of everything we do is our unwavering commitment to our customers. We strive to</li>
                      <li>1.<span className="text-orange-500">Strengthen Partnerships:</span> Build enduring relationships by aligning with the unique needs of our customers.</li>
                      <li>2.<span className="text-orange-500">Deliver Excellence:</span> Offer innovative products and services that deliver unmatched value, reliability, and performance.</li>
                      <li>Empower brands with advanced manufacturing solutions that enable them to scale, innovate, and lead in their respective markets.</li>
                    </ul>
                  </p>
                )}
                {index === 2 && (
                  <p className="text-gray-600 leading-relaxed">
                    <ul>
                      <li><span className="text-orange-500">Innovation, Excellence, and Trust</span></li>
                      <li>We stand for:</li>
                      <li><span className="text-orange-500">Integrity:</span> Building trust with every interaction and ensuring transparency in all our processes.</li>
                      <li><span className="text-orange-500">Customer-Centricity:</span> Prioritizing customer needs by offering seamless, hassle-free solutions designed for maximum convenience and value.</li>
                      <li><span className="text-orange-500">Inclusivity and Growth:</span> Expanding our portfolio of services to meet evolving needs, such as introducing no-fee checking and savings accounts with no minimum balance requirements—making our services accessible and inclusive for all.</li>
                      <li>Our values guide us as we continue to innovate, expand, and redefine industry standards, making us a reliable partner for brands worldwide.</li>
                    </ul>
                  </p>
                )}
                
                <div className="mt-6 w-12 h-1 bg-gradient-to-r from-orange-500 to-orange-400 rounded group-hover:w-16 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}